import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

/** POSUI.jsx
 * - Combined category tabs (AB..YZ, TOTS, WINE)
 * - Items grid (names only). Click to add.
 * - Cart with auto-save (debounced) to server.php/save-cart (session POS1)
 * - Mobile cart: No | Item | Qty | Amt | Del (hides @ on phones)
 * - Desktop cart: No | Item | Qty | @ | Amt | Del
 * - Qty/@/Amt/Del are a compact cluster on the RIGHT; Item takes all free space
 * - Cart width reduced on desktop (cart ~40%, items/actions ~60%)
 * - Plain numbers (no currency symbol)
 */

const API_BASE = "https://reactapp.kendiservers.com"; // change if needed
const CART_SESSION_ID = "POS1";

const categoryRanges = [
  { key: "AB", letters: ["A", "B"] }, { key: "CD", letters: ["C", "D"] },
  { key: "EF", letters: ["E", "F"] }, { key: "GH", letters: ["G", "H"] },
  { key: "IJ", letters: ["I", "J"] }, { key: "KL", letters: ["K", "L"] },
  { key: "MN", letters: ["M", "N"] }, { key: "OP", letters: ["O", "P"] },
  { key: "QR", letters: ["Q", "R"] }, { key: "ST", letters: ["S", "T"] },
  { key: "UV", letters: ["U", "V"] }, { key: "WX", letters: ["W", "X"] },
  { key: "YZ", letters: ["Y", "Z"] }, { key: "TOTS", letters: [] },
  { key: "WINE", letters: [] },
];

// number format (no currency symbol)
const fmt = (n) =>
  Number(n ?? 0).toLocaleString("en-KE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

// responsive hook
function useIsMobile(bp = 840) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= bp : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= bp);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [bp]);
  return isMobile;
}
const shadow = (z = 10, a = 0.15) => `0 ${Math.round(z / 2)}px ${z + 6}px rgba(0,0,0,${a})`;

export default function POSUI({ onBack }) {
  const isMobile = useIsMobile();

  // theme
  const c = {
    teal: "#0aa3a3", tealDark: "#067a7a", tealDeep: "#055a5a",
    bg: "#f2f7f9", panel: "#ffffff", border: "#dbe5ea",
    ink: "#0f172a", inkSoft: "#475569", danger: "#ef4444",
    accent: "#fde68a",
  };

  // layout
  const page = { minHeight: "100%", padding: isMobile ? 10 : 18, background: `linear-gradient(180deg, #f7fbfc 0%, ${c.bg} 100%)`, color: c.ink };
  const container = { maxWidth: 1140, margin: "0 auto" };
  const headerBar = { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 };
  const backBtn = { background: c.panel, border: `1px solid ${c.border}`, borderRadius: 12, padding: "8px 12px", cursor: "pointer", boxShadow: shadow(8, 0.08) };
  const title = { fontWeight: 900, letterSpacing: 0.4, color: c.tealDeep, fontSize: isMobile ? 16 : 18 };
  const cartBadge = { marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, background: c.panel, border: `1px solid ${c.border}`, borderRadius: 999, padding: isMobile ? "4px 8px" : "6px 10px", boxShadow: shadow(8, 0.08), fontWeight: 800, fontSize: isMobile ? 12 : 14 };
  const cartDot = { minWidth: isMobile ? 18 : 22, height: isMobile ? 18 : 22, borderRadius: 999, background: c.teal, color: "#fff", display: "grid", placeItems: "center", fontSize: isMobile ? 11 : 13 };

  // ↓↓ changed here: cart narrower on desktop/tablet
  const row = { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "0.8fr 1.2fr", gap: isMobile ? 10 : 16 };

  const panel = { border: `1px solid ${c.border}`, borderRadius: 16, background: c.panel, overflow: "hidden", boxShadow: shadow(14, 0.12) };
  const panelHeader = { padding: isMobile ? "8px 10px" : "12px 16px", borderBottom: `1px solid ${c.border}`, background: "linear-gradient(180deg, #ffffff 0%, #f5fafb 100%)", fontWeight: 700, letterSpacing: 0.2 };

  // tabs
  const [hoverTab, setHoverTab] = useState(null);
  const catBar = { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 };
  const catBtn = (key, active) => ({
    border: "none", cursor: "pointer", padding: isMobile ? "6px 9px" : "8px 12px",
    borderRadius: 16, fontWeight: 800, letterSpacing: 0.4, color: "#fff",
    boxShadow: active ? "inset 0 3px 9px rgba(0,0,0,0.22)" : "0 5px 0 #063e41, " + shadow(10, 0.18),
    transform: active ? "translateY(0px)" : hoverTab === key ? "translateY(-1px)" : "translateY(0)",
    transition: "transform 120ms ease, box-shadow 120ms ease",
    background: active ? `linear-gradient(180deg, ${c.teal} 0%, ${c.tealDark} 100%)` : `linear-gradient(180deg, ${c.teal} 0%, ${c.tealDeep} 100%)`,
    fontSize: isMobile ? 12 : 14,
  });

  // cart grids — cluster on the RIGHT with auto columns; Item grows with 1fr
  const cartColumnsDesktop = "48px 1fr auto auto auto auto"; // No | Item | Qty | @ | Amt | Del
  const cartColumnsMobile  = "32px 1fr auto auto auto";       // No | Item | Qty | Amt | Del

  const headRow = (isMobile
    ? { display: "grid", gridTemplateColumns: cartColumnsMobile, gap: 8, ...panelHeader }
    : { display: "grid", gridTemplateColumns: cartColumnsDesktop, gap: 8, ...panelHeader });

  const body = { maxHeight: isMobile ? 420 : 520, overflowY: "auto" };

  const line = (isMobile
    ? { display: "grid", gridTemplateColumns: cartColumnsMobile, gap: 8, alignItems: "center", padding: "10px 10px", borderBottom: `1px dashed ${c.border}` }
    : { display: "grid", gridTemplateColumns: cartColumnsDesktop, gap: 8, alignItems: "center", padding: "12px 16px", borderBottom: `1px dashed ${c.border}` });

  const qtyInput = { width: 40, textAlign: "center", padding: "4px", borderRadius: 8, border: `1px solid ${c.border}`, boxShadow: "inset 0 2px 6px rgba(0,0,0,0.06)", fontWeight: 600, fontSize: isMobile ? 13 : 14 };

  const softLabel = { color: c.inkSoft, fontSize: isMobile ? 12 : 14 };
  const strong = { fontWeight: 800 };

  const footer = { display: "flex", gap: 8, alignItems: "center", padding: isMobile ? "8px 10px" : "12px 16px", background: "#fbfeff", borderTop: `1px solid ${c.border}`, flexWrap: "wrap" };
  const ghostBtn = { padding: isMobile ? "7px 10px" : "9px 12px", borderRadius: 12, border: `1px solid ${c.border}`, background: "#fff", cursor: "pointer", boxShadow: shadow(8, 0.08), fontSize: isMobile ? 13 : 14 };
  const dueBox = { marginLeft: isMobile ? 0 : "auto", display: "flex", alignItems: "center", gap: 10, fontWeight: 800, fontSize: isMobile ? 14 : 16 };
  const dueBadge = { background: `linear-gradient(180deg, ${c.accent} 0%, #facc15 100%)`, color: "#111827", borderRadius: 12, padding: isMobile ? "6px 12px" : "7px 14px", boxShadow: shadow(10, 0.18), minWidth: isMobile ? 90 : 120, textAlign: "center" };

  // items grid
  const listWrapPad = { padding: 10 };
  const listGrid = { display: "grid", gap: 10, gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)" };
  const card = { display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", minHeight: 60, padding: "12px 10px", borderRadius: 14, border: `1px solid ${c.border}`, background: "linear-gradient(180deg, #ffffff 0%, #f9feff 100%)", boxShadow: shadow(10, 0.12), cursor: "pointer", fontWeight: 800, lineHeight: 1.2, fontSize: isMobile ? 12 : 14 };
  const disabledCard = { ...card, color: "#999", cursor: "not-allowed", filter: "grayscale(0.25)", opacity: 0.8 };

  const rightButtons = { display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "1fr", gap: 10, padding: 12 };
  const actionBtn = { ...ghostBtn, textAlign: "center", background: "linear-gradient(180deg, #ffffff 0%, #f8fbfc 100%)" };

  // data
  const [items, setItems] = useState([]); // {id,name,price,stock,cat}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeCat, setActiveCat] = useState("ALL");
  const [cart, setCart] = useState([]);   // {id,name,price,qty}

  const total = cart.reduce((s, r) => s + r.price * r.qty, 0);
  const count = cart.reduce((s, r) => s + r.qty, 0);

  // auto-save (debounced)
  const saveTimer = useRef(null);
  const flushSave = async (data) => {
    try {
      await axios.post(`${API_BASE}/server.php/save-cart`, {
        session_id: CART_SESSION_ID,
        cart: data.map(c => ({ id: c.id, itemname: c.name, price: c.price, quantity: c.qty })),
      });
    } catch (e) { console.warn("Cart save failed:", e?.message || e); }
  };
  const debounceSave = (data) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => flushSave(data), 200);
  };
  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  const resolveCategoryKey = (name) => {
    const f = (name || "").charAt(0).toUpperCase();
    if (/TOTS?/i.test(name)) return "TOTS";
    if (/WINE/i.test(name)) return "WINE";
    const found = categoryRanges.find((r) => r.letters.includes(f));
    return found ? found.key : "#";
  };

  const loadInventory = async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.get(`${API_BASE}/server.php/inventory`);
    const normalized = (res.data || []).map((row, idx) => {
        const id = row.id ?? idx + 1;
        const name = String(row.itemname ?? row.name ?? "").trim();
        const price = Number(row.sellingprice ?? row.price ?? 0);
        const stock = Number(row.quantity ?? 0);
        const cat = resolveCategoryKey(name);
        return { id, name, price, stock, cat };
      });
      setItems(normalized);
    } catch (e) { console.error(e); setError("⚠️ Could not load inventory"); }
    finally { setLoading(false); }
  };

  const loadCartFromDB = async () => {
    try {
      const res = await axios.get(`${API_BASE}/server.php/load-cart/${CART_SESSION_ID}`);
      const saved = res.data?.cart || [];
      const normalized = saved.map((i, idx) => ({
        id: i.id ?? idx + 1,
        name: i.name ?? i.itemname ?? "",
        price: Number(i.price ?? i.sellingprice ?? 0),
        qty: Number(i.qty ?? i.quantity ?? 0),
      })).filter(i => i.name);
      if (normalized.length) setCart(normalized);
    } catch (e) { console.warn("Cart load failed:", e?.message || e); }
  };

  useEffect(() => { loadInventory(); loadCartFromDB(); }, []);
  useEffect(() => { debounceSave(cart); }, [cart]); // autosave

  const categories = useMemo(() => ["ALL", ...categoryRanges.map((r) => r.key)], []);
  const visible = useMemo(() => (activeCat === "ALL" ? items : items.filter((i) => i.cat === activeCat)), [activeCat, items]);

  // cart ops
  const addToCart = (it) => {
    const inCart = cart.find((c) => c.id === it.id)?.qty ?? 0;
    if (inCart >= (it.stock ?? 0)) return alert("❌ Cannot add more — stock limit reached!");
    setCart((prev) => {
      const found = prev.find((r) => r.id === it.id);
      return found ? prev.map((r) => (r.id === it.id ? { ...r, qty: r.qty + 1 } : r))
                   : [...prev, { id: it.id, name: it.name, price: it.price, qty: 1 }];
    });
  };
  const updateQty = (id, qty) => setCart((prev) => prev.map((r) => (r.id === id ? { ...r, qty: Math.max(1, qty) } : r)));
  const removeRow = (id) => setCart((prev) => prev.filter((r) => r.id !== id));
  const clearCart = () => setCart([]);

  const checkout = async () => {
    if (cart.length === 0) return alert("Cart is empty");
    try {
      const payload = { cart: cart.map((c) => ({ id: c.id, itemname: c.name, quantity: c.qty, price: c.price })), total, paid: total, balance: 0 };
      const res = await axios.post(`${API_BASE}/server.php/sale`, payload);
      if (res.data?.success) { alert(`Sale completed! ID: ${res.data.saleId}`); setCart([]); await loadInventory(); }
      else { alert(res.data?.message || "Sale failed"); }
    } catch (e) { console.error(e); alert("Error saving sale"); }
  };

  // render
  return (
    <div style={page}>
      <div style={container}>
        <div style={headerBar}>
          {onBack && <button style={backBtn} onClick={onBack}>← Back to Menu</button>}
          <div style={title}>Point of Sale</div>
          <div style={cartBadge} title={`${count} item(s) • ${fmt(total)}`}>
            <span>Cart</span><span style={cartDot}>{count}</span>
            <span style={{ color: c.inkSoft }}>{fmt(total)}</span>
          </div>
        </div>

        {/* tabs */}
        <div style={catBar}>
          {["ALL", ...categoryRanges.map((r) => r.key)].map((k) => (
            <button key={k} style={catBtn(k, activeCat === k)}
              onMouseEnter={() => setHoverTab(k)} onMouseLeave={() => setHoverTab(null)}
              onClick={() => setActiveCat(k)}>{k}</button>
          ))}
        </div>

        <div style={row}>
          {/* LEFT — CART (now narrower) */}
          <div style={panel}>
            <div style={headRow}>
              <div>No</div>
              <div style={{ textAlign: "left" }}>Item Description</div>
              <div style={{ textAlign: "center" }}>Qty</div>
              {!isMobile && <div style={{ textAlign: "center" }}>@</div>}
              <div style={{ textAlign: "right" }}>Amt</div>
              <div style={{ textAlign: "center" }}>Del</div>
            </div>

            <div style={body}>
              {cart.length === 0 ? (
                <div style={{ padding: 12, color: c.inkSoft }}>No items yet. Click a product to add.</div>
              ) : (
                cart.map((r, i) => (
                  <div key={r.id} style={line}>
                    <div style={softLabel}>{i + 1}</div>

                    <div title={r.name}
                      style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.35em", fontWeight: 700 }}>
                      {r.name}
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <input type="number" min={1} value={r.qty}
                        onChange={(e) => updateQty(r.id, Number(e.target.value))}
                        style={qtyInput}/>
                    </div>

                    {!isMobile && (
                      <div style={{ textAlign: "center", color: c.inkSoft }}>{fmt(r.price)}</div>
                    )}

                    <div style={{ textAlign: "right", ...strong }}>{fmt(r.price * r.qty)}</div>

                    <div style={{ textAlign: "center" }}>
                      <button
                        style={{
                          padding: isMobile ? "6px 8px" : "9px 12px",
                          borderRadius: 12,
                          border: `1px solid #f5c5c5`,
                          background: "#fff5f5",
                          color: c.danger,
                          cursor: "pointer",
                        }}
                        onClick={() => removeRow(r.id)}
                      >
                        {isMobile ? "✕" : "Del"}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div style={footer}>
              <button style={ghostBtn} onClick={() => window.print()}>🖨 Print</button>
              <button style={{ ...ghostBtn, background: "#fff8f8", color: c.danger }} onClick={clearCart}>✖ Clear Cart</button>
              <div style={dueBox}>
                <span>AMOUNT DUE</span>
                <span style={dueBadge}>{fmt(total)}</span>
              </div>
            </div>
          </div>

          {/* RIGHT — ITEMS + ACTIONS (now wider) */}
          <div style={{ display: "grid", gap: 10 }}>
            <div style={panel}>
              <div style={panelHeader}>Items</div>
              <div style={{ ...listWrapPad, maxHeight: isMobile ? 420 : 560, overflowY: "auto" }}>
                {loading && <div style={{ padding: 10, color: c.inkSoft }}>Loading items…</div>}
                {error && <div style={{ padding: 10, color: c.danger }}>{error}</div>}
                {!loading && !error && (
                  <div style={listGrid}>
                    {visible.map((it) => {
                      const disabled = it.stock <= 0;
                      return (
                        <button key={it.id} title={disabled ? "Out of Stock" : ""}
                          onClick={() => !disabled && addToCart(it)}
                          style={disabled ? disabledCard : card}>
                          {it.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div style={panel}>
              <div style={panelHeader}>Actions</div>
              <div style={rightButtons}>
                <button style={actionBtn} onClick={() => alert("Orders list…")}>View Orders</button>
                <button style={{ ...actionBtn, background: `linear-gradient(180deg, ${c.teal} 0%, ${c.tealDark} 100%)`, color: "#fff" }} onClick={checkout}>
                  Checkout / Save Sale
                </button>
                <button style={actionBtn}
                  onClick={async () => {
                    const res = await axios.get(`${API_BASE}/server.php/reports`).catch(() => null);
                    alert(res ? JSON.stringify(res.data, null, 2) : "Failed to load reports");
                  }}>
                  Reports
                </button>
                <button style={actionBtn}
                  onClick={async () => {
                    const res = await axios.get(`${API_BASE}/server.php/stock-movements`).catch(() => null);
                    alert(res ? JSON.stringify(res.data, null, 2) : "Failed to load stock movements");
                  }}>
                  Stock Movements
                </button>
                <button style={actionBtn} onClick={() => alert("Logging out…")}>Log Out</button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 10, color: c.inkSoft, fontSize: ".95rem" }}>
          Current User: <b>MORITA</b> &nbsp;•&nbsp; © Kevsoft POS
        </div>
      </div>
    </div>
  );
}
